// src/components/union-settings/banks/UnionBanksManagement.tsx
// src/components/union-settings/banks/UnionBanksManagement.tsx
import {
  Alert,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  Separator,
  Text,
  VStack,
  Menu,
  SwitchHiddenInput,
  SwitchControl,
  SwitchThumb,
  SwitchRoot,
  SwitchThumbIndicator,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import {
  LuPlus,
  LuTextSelect,
  LuPencil,
  LuTrash2,
  LuBadgePercent,
  LuListFilter,
  LuCheck,
} from 'react-icons/lu'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import {
  createBank,
  updateBank,
  patchBankStatus,
  upsertBankDiscount,
  deleteBankDiscount,
  getBankDiscountOrNull,
  createBankDiscount,
  getUnionBankById,
} from '@/api/settings.api'
import type { CreateOrUpdateBankPayload } from '@/api/dto/settings.dto'
import type { UnionBank } from '@/common/unionBank.common'
import { useUserStore } from '@/stores/user.store'
import UnionBankWizardDialog from './dialogs/UnionBankWizardDialog'
import UnionDeleteBankDialog from './dialogs/UnionDeleteBankDialog'
import UnionBankDiscountDialog from './dialogs/UnionBankDiscountDialog'
import { HiCheck, HiX } from 'react-icons/hi'
import { useBanksStore } from '@/stores/banks.store'

type StatusFilter = 'ALL' | 'ACTIVE' | 'INACTIVE'
type SortBy = 'ALPHABETISCH' | 'COMPANY_CODE'
type SortDir = 'ASC' | 'DESC'

type FilterState = {
  status: StatusFilter
  sortBy: SortBy
  sortDir: SortDir
}

function getIsActive(b: UnionBank): boolean {
  return !!b.active
}

function companyCode(b: UnionBank): string {
  // Mock says “Company Code”. Backend currently has accountingArea (null in payload).
  return (b.accountingArea ?? '').trim()
}

export default function UnionBanksManagement() {
  const { user } = useUserStore()
  const role = String(user?.role ?? '')
  const canManage = role.includes('manager') // keeps SIB read-only by default

  const banks = useBanksStore(s => s.banks) as UnionBank[]
  const loadingMore = useBanksStore(s => s.loadingMore)
  const addBank = useBanksStore(s => s.addBank)
  const replaceBank = useBanksStore(s => s.replaceBank)
  const patchBankLocally = useBanksStore(s => s.patchBankLocally)
  const removeBank = useBanksStore(s => s.removeBank)

  const loading = useBanksStore(s => s.loading)
  const error = useBanksStore(s => s.error)
  const ensureLoaded = useBanksStore(s => s.ensureLoaded)
  const reload = useBanksStore(s => s.reload)
  useEffect(() => {
    ensureLoaded({ parentId: 1, includeInactive: canManage }).catch(() => {})
  }, [canManage, ensureLoaded])

  const [selected, setSelected] = useState<UnionBank | null>(null)

  const [discountLoading, setDiscountLoading] = useState(false)
  const [discountPct, setDiscountPct] = useState<number | null>(null)
  const [discountExists, setDiscountExists] = useState(false)

  // Fetch full bank details when selected bank changes
  const [bankDetails, setBankDetails] = useState<UnionBank | null>(null)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [detailsError, setDetailsError] = useState<string | null>(null)

  useEffect(() => {
    if (!selected) {
      setBankDetails(null)
      setDetailsError(null)
      setDetailsLoading(false)
      return
    }

    let cancelled = false
    ;(async () => {
      setDetailsLoading(true)
      setDetailsError(null)
      try {
        const full = await getUnionBankById(selected.id)
        if (!cancelled) setBankDetails(full)
      } catch {
        if (!cancelled) {
          setBankDetails(null)
          setDetailsError('Fehler beim Laden der Bankdetails.')
        }
      } finally {
        if (!cancelled) setDetailsLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [selected?.id])
  const details = bankDetails ?? selected // fallback (optional)
  const detailsActive = !!details?.active
  const d = bankDetails ?? selected
  function fmtDateTime(v?: string | null) {
    if (!v) return '—'
    try {
      return new Date(v).toLocaleString('de-DE')
    } catch {
      return v
    }
  }

  // Fetch discount when selected bank changes
  useEffect(() => {
    if (!canManage || !selected) return

    let cancelled = false
    ;(async () => {
      setDiscountLoading(true)
      try {
        const d = await getBankDiscountOrNull(selected.id)
        if (cancelled) return
        setDiscountExists(!!d)
        setDiscountPct(d?.percentage ?? null)
      } catch {
        if (cancelled) return
        setDiscountExists(false)
        setDiscountPct(null)
      } finally {
        if (!cancelled) setDiscountLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [canManage, selected?.id])

  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<FilterState>({
    status: 'ALL',
    sortBy: 'ALPHABETISCH',
    sortDir: 'ASC',
  })

  async function refreshBanks() {
    await reload({ parentId: 1, includeInactive: canManage })
  }

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [discountOpen, setDiscountOpen] = useState(false)

  function getDeactivateBankErrorMessage(err: any) {
    const status = err?.response?.status ?? err?.status
    const backendMessage = err?.response?.data?.message ?? err?.data?.message ?? err?.message ?? ''

    if (status === 409) {
      if (
        backendMessage.includes('open or corrected orders') ||
        backendMessage.includes(
          'Cannot deactivate bank because there are open or corrected orders for it'
        )
      ) {
        return 'Bank kann nicht deaktiviert werden, da offene oder korrigierte Bestellungen dafür vorhanden sind.'
      }

      if (
        backendMessage.includes('approved orders with a future end date') ||
        backendMessage.includes(
          'Cannot deactivate bank because there are approved orders with a future end date for it'
        )
      ) {
        return 'Bank kann nicht deaktiviert werden, da freigegebene Bestellungen mit zukünftigem Enddatum dafür vorhanden sind.'
      }

      return backendMessage || 'Bank kann aktuell nicht deaktiviert werden.'
    }

    return 'Status konnte nicht gespeichert werden.'
  }

  async function toggleActive(next: boolean) {
    if (!selected) return

    try {
      await patchBankStatus(selected.id, next)
      patchBankLocally(selected.id, { active: next } as any)
      setSelected(prev => (prev ? { ...prev, active: next } : prev))
      setBankDetails(prev => (prev ? { ...prev, active: next } : prev))
      toast.success(next ? 'Bank aktiviert' : 'Bank deaktiviert')
    } catch (err: any) {
      toast.error(
        'Fehler',
        next ? 'Status konnte nicht gespeichert werden.' : getDeactivateBankErrorMessage(err)
      )
    }
  }

  useEffect(() => {
    setSelected(prev => {
      if (!prev) return null
      return banks.find(x => x.id === prev.id) ?? null
    })
  }, [banks])

  function toggleStatusFilter(next: Exclude<StatusFilter, 'ALL'>) {
    setFilters(prev => ({
      ...prev,
      status: prev.status === next ? 'ALL' : next,
    }))
  }

  const COUNTRY_LABELS: Record<string, string> = {
    de: 'DE',
    at: 'AT',
    ch: 'CH',
  }

  const MISSING_FIELD_LABELS: Record<string, string> = {
    name: 'Name',
    name2: 'Name 2',
    name3: 'Name 3',
    street: 'Straße',
    streetNumber: 'Hausnummer',
    postal: 'PLZ',
    city: 'Ort',
    country: 'Land',
    sapDebtorId: 'Debitor',
    businessPartner: 'Geschäftspartner',
    blzNum: 'BLZ',
    accountingArea: 'Buchungskreis',
    centralBookingBlock: 'Zentr. Buchungssperre',
    vatNumber: 'USt-IdNr.',
  }

  function labelForMissingField(field: string) {
    return MISSING_FIELD_LABELS[field] ?? field
  }

  const WARNING_TRANSLATIONS: { match: RegExp; de: string }[] = [
    {
      match: /^A bank with the same name already exists under the union$/i,
      de: 'Eine Bank mit demselben Namen existiert bereits unter der Union.',
    },
  ]

  function translateWarning(warning: string) {
    const trimmed = warning.trim()
    for (const t of WARNING_TRANSLATIONS) {
      if (t.match.test(trimmed)) return t.de
    }
    return warning
  }

  function getCountryLabel(country?: string | null) {
    if (!country) return '—'
    return COUNTRY_LABELS[country.toLowerCase()] ?? country
  }

  const viewItems = useMemo(() => {
    // If backend doesn’t support all params, we still enforce client-side view:
    let items = [...banks]

    // search client-side (extra safety)
    // const s = search.trim().toLowerCase()
    // if (s) {
    //   items = items.filter(b => {
    //     const addr = `${b.postal ?? ''} ${b.city ?? ''} ${b.country ?? ''}`.toLowerCase()
    //     return b.name.toLowerCase().includes(s) || addr.includes(s)
    //   })
    // }

    const s = search.trim().toLowerCase()
    if (s) {
      items = items.filter(b => {
        const addr = `${b.postal ?? ''} ${b.city ?? ''} ${b.country ?? ''}`.toLowerCase()
        const debitor = String((b as any)?.sapDebtorId ?? '').toLowerCase()
        const blz = String((b as any)?.blzNum ?? '').toLowerCase()

        return (
          b.name.toLowerCase().includes(s) ||
          addr.includes(s) ||
          debitor.includes(s) ||
          blz.includes(s)
        )
      })
    }

    // status
    if (canManage && filters.status !== 'ALL') {
      items = items.filter(b => (filters.status === 'ACTIVE' ? getIsActive(b) : !getIsActive(b)))
    } else if (!canManage) {
      items = items.filter(b => getIsActive(b))
    }

    // sorting
    const dir = filters.sortDir === 'ASC' ? 1 : -1
    items.sort((a, b) => {
      const aKey =
        filters.sortBy === 'COMPANY_CODE' ? companyCode(a).toLowerCase() : a.name.toLowerCase()
      const bKey =
        filters.sortBy === 'COMPANY_CODE' ? companyCode(b).toLowerCase() : b.name.toLowerCase()

      if (aKey < bKey) return -1 * dir
      if (aKey > bKey) return 1 * dir
      return 0
    })

    return items
  }, [banks, canManage, filters.sortBy, filters.sortDir, filters.status, search])

  const selectedActive = selected ? getIsActive(selected) : true
  const toNullable = (value?: string | null) => {
    const trimmed = value?.trim()
    return trimmed ? trimmed : null
  }

  return (
    <Flex data-testid="bank-management-page" direction="column" gap={4} p={4}>
      {/* Top bar (like mock) — the button's negative vertical margin keeps this
          row at headline height, so the headline and the search bar below sit at
          the same heights as on screens without a header button (SIB-874) */}
      <Flex justify="space-between" align="center">
        <Heading size="lg" fontWeight="normal">
          Banken Management
        </Heading>

        {canManage && (
          <Button
            size="sm"
            variant="subtle"
            bg="blue.subtle"
            borderWidth="1px"
            color="blue.fg"
            borderColor="blue.muted"
            my="-1"
            onClick={() => setCreateOpen(true)}
          >
            <LuPlus /> Neue Bank anlegen
          </Button>
        )}
      </Flex>

      {/* Search + filter row */}
      <HStack w="full" gap={3} align="center" flexWrap="wrap">
        <Input
          flex="1"
          minW="240px"
          placeholder="Banken durchsuchen, Name oder Ort eingeben"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        <Menu.Root>
          <Menu.Trigger asChild>
            <Button
              variant={filters.status !== 'ALL' ? 'solid' : 'subtle'}
              colorPalette={filters.status !== 'ALL' ? 'teal' : 'gray'}
              size="sm"
            >
              <HStack gap={2}>
                <LuListFilter />
                <Text>Filter</Text>
              </HStack>
            </Button>
          </Menu.Trigger>

          <Menu.Positioner>
            <Menu.Content
              minW="260px"
              p={0}
              borderRadius="lg"
              borderWidth="1px"
              borderColor="border.muted"
              overflow="hidden"
            >
              {/* Group 1 (exact order like mockup) */}
              <Menu.Item
                value="alphabetisch"
                onClick={() => setFilters(s => ({ ...s, sortBy: 'ALPHABETISCH' }))}
                px={4}
                py={3}
                fontSize="sm"
                fontWeight="normal"
              >
                <Flex w="full" align="center" justify="space-between">
                  <Text>Alphabetisch</Text>
                  {filters.sortBy === 'ALPHABETISCH' && <LuCheck />}
                </Flex>
              </Menu.Item>

              <Menu.Item
                value="aktiv"
                onClick={() => toggleStatusFilter('ACTIVE')}
                px={4}
                py={3}
                fontSize="sm"
                fontWeight="normal"
              >
                <Flex w="full" align="center" justify="space-between">
                  <Text>Aktiv</Text>
                  {filters.status === 'ACTIVE' && <LuCheck />}
                </Flex>
              </Menu.Item>

              <Menu.Item
                value="inaktiv"
                onClick={() => toggleStatusFilter('INACTIVE')}
                px={4}
                py={3}
                fontSize="sm"
                fontWeight="normal"
              >
                <Flex w="full" align="center" justify="space-between">
                  <Text>Inaktiv</Text>
                  {filters.status === 'INACTIVE' && <LuCheck />}
                </Flex>
              </Menu.Item>
              {/* <Menu.Item
                value="alle-status"
                onClick={() => setFilters(s => ({ ...s, status: 'ALL' }))}
                px={4}
                py={3}
                fontSize="sm"
                fontWeight="normal"
              >
                <Flex w="full" align="center" justify="space-between">
                  <Text>Alle Status</Text>
                  {filters.status === 'ALL' && <LuCheck />}
                </Flex>
              </Menu.Item> */}
              {/* <Menu.Item
                value="companycode"
                onClick={() => setFilters(s => ({ ...s, sortBy: 'COMPANY_CODE' }))}
                px={4}
                py={3}
                fontSize="sm"
                fontWeight="normal"
              >
                <Flex w="full" align="center" justify="space-between">
                  <Text>Company Code</Text>
                  {filters.sortBy === 'COMPANY_CODE' && <LuCheck />}
                </Flex>
              </Menu.Item> */}

              {/* Divider like mockup */}
              <Menu.Separator />

              {/* Group 2 */}
              <Menu.Item
                value="absteigend"
                onClick={() => setFilters(s => ({ ...s, sortDir: 'DESC' }))}
                px={4}
                py={3}
                fontSize="sm"
                fontWeight="normal"
              >
                <Flex w="full" align="center" justify="space-between">
                  <Text>Absteigend</Text>
                  {filters.sortDir === 'DESC' && <LuCheck />}
                </Flex>
              </Menu.Item>

              <Menu.Item
                value="aufsteigend"
                onClick={() => setFilters(s => ({ ...s, sortDir: 'ASC' }))}
                px={4}
                py={3}
                fontSize="sm"
                fontWeight="normal"
              >
                <Flex w="full" align="center" justify="space-between">
                  <Text>Aufsteigend</Text>
                  {filters.sortDir === 'ASC' && <LuCheck />}
                </Flex>
              </Menu.Item>
            </Menu.Content>
          </Menu.Positioner>
        </Menu.Root>
      </HStack>

      {/* Main split view */}
      <Flex borderRadius="xl" borderWidth="1px" borderColor="border" overflow="hidden" bg="bg">
        {/* LEFT LIST */}
        <Box
          w="340px"
          borderRightWidth="1px"
          borderRightColor="border"
          maxH="480px"
          overflowY="auto"
        >
          {loading && (
            <VStack align="stretch" gap={0}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Box key={i} p={4} borderBottomWidth="1px" borderBottomColor="border.muted">
                  <Box h="12px" w="70%" bg="bg.muted" borderRadius="md" mb={2} />
                  <Box h="10px" w="90%" bg="bg.subtle" borderRadius="md" />
                </Box>
              ))}
            </VStack>
          )}

          {!loading && error && (
            <Box p={4}>
              <Text color="fg.error" fontSize="sm">
                {error}
              </Text>
              <Button mt={3} size="sm" variant="outline" onClick={refreshBanks}>
                Erneut versuchen
              </Button>
            </Box>
          )}

          {!loading && !error && viewItems.length === 0 && (
            <Box p={4}>
              <Text color="fg.muted" fontSize="sm">
                Keine Banken gefunden.
              </Text>
            </Box>
          )}

          {!loading && !error && viewItems.length > 0 && (
            <VStack align="stretch" gap={0}>
              {viewItems.map(b => {
                const isSel = selected?.id === b.id
                const active = getIsActive(b)

                return (
                  <Box
                    key={b.id}
                    data-testid={`bank-management-row-${b.id}`}
                    p={4}
                    cursor="pointer"
                    bg={isSel ? 'bg.muted' : 'bg'}
                    borderBottomWidth="1px"
                    borderBottomColor="border.muted"
                    _hover={{ bg: 'bg.subtle' }}
                    onClick={() => setSelected(b)}
                  >
                    <Flex justify="space-between" align="flex-start" gap={3}>
                      <Box>
                        <Text fontWeight="500" fontSize="md">
                          {b.name} {b.name2} {b.name3}
                        </Text>
                        <Text fontSize="sm" color="fg.muted" fontWeight="normal">
                          {b.postal} {b.city} {getCountryLabel(b.country)}
                        </Text>
                      </Box>

                      <VStack align="flex-end" gap={1} flexShrink={0} mt={1}>
                        <Badge size="sm" variant="subtle" colorPalette={active ? 'green' : 'gray'}>
                          {active ? 'Aktiviert' : 'Deaktiviert'}
                        </Badge>
                        {b.incomplete && (
                          <Badge size="sm" variant="subtle" colorPalette="yellow">
                            Unvollständig
                          </Badge>
                        )}
                      </VStack>
                    </Flex>
                  </Box>
                )
              })}

              {loadingMore && (
                <Box p={4}>
                  <Text fontSize="sm" color="fg.muted">
                    Weitere Banken werden geladen…
                  </Text>
                </Box>
              )}
            </VStack>
          )}
        </Box>

        {/* RIGHT DETAILS */}
        <Box flex="1" p={5}>
          {!selected && (
            <Flex
              direction="column"
              align="center"
              justify="center"
              minH="420px"
              textAlign="center"
              color="fg.muted"
              gap={3}
            >
              <Box opacity={0.5}>
                <LuTextSelect size={32} />
              </Box>
              <Text fontSize="lg" fontWeight="700" color="fg.default">
                Keine Bank ausgewählt
              </Text>
              <Text maxW="420px" fontSize="sm" color="fg.muted">
                Bitte wählen Sie eine Bank aus der linken Spalte, um Details und Aktionen zu sehen.
              </Text>
            </Flex>
          )}
          {selected && detailsLoading && (
            <Box py={6}>
              <Text fontSize="sm" color="fg.muted">
                Lade Bankdetails…
              </Text>
            </Box>
          )}

          {selected && !detailsLoading && detailsError && (
            <Box py={6}>
              <Text fontSize="sm" color="fg.error">
                {detailsError}
              </Text>
            </Box>
          )}
          {selected && (
            <Box>
              <Flex justify="space-between" align="flex-start" gap={3} mb={2}>
                <Box>
                  <Text fontSize="lg" fontWeight="semibold">
                    {selected.name}&nbsp;{selected.name2}&nbsp;{selected.name3}
                  </Text>
                  {/* <Text fontSize="sm" color="fg.muted">
                    {selected.street} {selected.streetNumber}, {selected.postal} {selected.city},{' '}
                    {selected.country}
                  </Text> */}
                  {canManage && (
                    <HStack gap={2} align="center" mt="2">
                      <Text fontSize="sm" color="fg.muted">
                        {selectedActive ? 'Bank aktiviert' : 'Inaktiv'}
                      </Text>

                      <SwitchRoot
                        size="md"
                        colorPalette="green"
                        // checked={selected.active}
                        checked={detailsActive}
                        style={{ touchAction: 'none' }}
                        onCheckedChange={(detail: { checked: boolean }) =>
                          toggleActive(detail.checked)
                        }
                      >
                        <SwitchHiddenInput />
                        <SwitchControl>
                          <SwitchThumb>
                            <SwitchThumbIndicator fallback={<HiX color="black" />}>
                              <HiCheck />
                            </SwitchThumbIndicator>
                          </SwitchThumb>
                        </SwitchControl>
                      </SwitchRoot>
                    </HStack>
                  )}
                </Box>

                <HStack gap={2}>
                  {canManage && (
                    <>
                      <Button size="xs" variant="plain" onClick={() => setDiscountOpen(true)}>
                        <LuBadgePercent />
                        Rabatt einstellen
                      </Button>

                      <IconButton
                        aria-label="edit"
                        size="xs"
                        variant="ghost"
                        colorPalette="blue"
                        onClick={() => setEditOpen(true)}
                      >
                        <LuPencil />
                      </IconButton>

                      <IconButton
                        aria-label="delete"
                        size="xs"
                        variant="ghost"
                        colorPalette="red"
                        onClick={() => setDeleteOpen(true)}
                      >
                        <LuTrash2 />
                      </IconButton>
                    </>
                  )}
                </HStack>
              </Flex>

              <Separator />

              {/* Incomplete / missing fields callout */}
              {d?.incomplete && (
                <Alert.Root
                  status="warning"
                  variant="surface"
                  borderRadius="l3"
                  p={3}
                  gap={2}
                  mt={3}
                  bg="yellow.subtle"
                  colorPalette="yellow"
                >
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Title fontSize="xs" fontWeight="semibold">
                      Stammdaten unvollständig
                    </Alert.Title>
                    {d?.missingFields && d.missingFields.length > 0 && (
                      <Alert.Description fontSize="xs">
                        Fehlende Felder: {d.missingFields.map(labelForMissingField).join(', ')}
                      </Alert.Description>
                    )}
                  </Alert.Content>
                </Alert.Root>
              )}

              {/* Warnings callout (e.g. duplicate name) */}
              {d?.warnings && d.warnings.length > 0 && (
                <Alert.Root
                  status="info"
                  variant="surface"
                  borderRadius="l3"
                  p={3}
                  gap={2}
                  mt={3}
                  bg="blue.subtle"
                  colorPalette="blue"
                >
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Title fontSize="xs" fontWeight="semibold">
                      Hinweise
                    </Alert.Title>
                    <Alert.Description fontSize="xs">
                      {d.warnings.map((w, i) => (
                        <Text key={i}>{translateWarning(w)}</Text>
                      ))}
                    </Alert.Description>
                  </Alert.Content>
                </Alert.Root>
              )}

              {/* Details grid */}
              <VStack align="stretch" gap={0} mt={3} maxH="380px" overflowY="auto">
                <DetailRow label="Name" value={d?.name ?? '—'} />
                <Separator />

                <DetailRow label="Name 2" value={d?.name2 ?? '—'} />
                <Separator />

                <DetailRow label="Name 3" value={d?.name3 ?? '—'} />
                <Separator />

                <DetailRow label="Straße" value={d?.street ?? '—'} />
                <Separator />

                <DetailRow label="Hausnr." value={d?.streetNumber ?? '—'} />
                <Separator />

                <DetailRow label="Vertriebsgebiet." value={d?.salesRegion ?? '—'} />
                <Separator />

                <DetailRow label="PLZ" value={d?.postal ?? '—'} />
                <Separator />

                <DetailRow label="Stadt" value={d?.city ?? '—'} />
                <Separator />

                <DetailRow label="Land" value={getCountryLabel((d as any)?.country)} />
                <Separator />

                <DetailRow label="Debitor" value={(d as any)?.sapDebtorId ?? '—'} />
                <Separator />

                <DetailRow label="Geschäftspartner" value={(d as any)?.businessPartner ?? '—'} />
                <Separator />

                <DetailRow
                  label="Zentr. Buchungssperre"
                  value={(d as any)?.centralBookingBlock ?? '—'}
                />
                <Separator />

                <DetailRow label="Buchungskreis" value={(d as any)?.accountingArea ?? '—'} />
                <Separator />

                {/* <DetailRow label="USt-IdNr." value={(d as any)?.vatNumber ?? '—'} />
                <Separator />
                 */}
                <DetailRow label="BLZ" value={(d as any)?.blzNum ?? '—'} />
                <Separator />

                {/* contactDetails (Ansprechpartner) is user-specific and hidden
                    from the Union view — see SIB-876 */}
                <DetailRow label="Interne Kontakt-E-Mail" value={d?.internalContactEmail ?? '—'} />
                <Separator />

                <DetailRow label="Status" value={detailsActive ? 'Aktiviert' : 'Deaktiviert'} />
                <Separator />

                <DetailRow
                  label="Erstellt am"
                  value={fmtDateTime((d as any)?.createdAt ?? null)}
                />
                <Separator />

                <DetailRow
                  label="Aktualisiert am"
                  value={fmtDateTime((d as any)?.updatedAt ?? null)}
                />
              </VStack>

              {/* dialogs */}

              <UnionBankWizardDialog
                mode="edit"
                open={editOpen}
                onOpenChange={setEditOpen}
                initial={bankDetails ?? selected}
                onSubmit={async form => {
                  if (!selected) return

                  // base has the most complete data you currently have
                  const base = (bankDetails ?? selected) as any

                  const payload: CreateOrUpdateBankPayload = {
                    name: form.name?.trim() || base.name,
                    name2: (form.name2 ?? base.name2)?.trim() || null,
                    name3: (form.name3 ?? base.name3)?.trim() || null,

                    street: form.street?.trim() || base.street,
                    streetNumber: form.streetNumber?.trim() || base.streetNumber,
                    postal: form.postal?.trim() || base.postal,
                    salesRegion: form.salesRegion?.trim() || base.salesRegion,
                    city: form.city?.trim() || base.city,
                    country: form.country?.trim() || base.country,

                    // contactDetails is user-specific — Union must not touch it (SIB-876)
                    contactDetails: null,
                    internalContactEmail: toNullable(form.internalContactEmail),

                    sapDebtorId: (form.sapDebtorId ?? base.sapDebtorId)?.trim() || null,
                    businessPartner:
                      (form.businessPartner ?? base.businessPartner)?.trim() || null,
                    centralBookingBlock:
                      (form.centralBookingBlock ?? base.centralBookingBlock)?.trim() || null,
                    blzNum: form.blz?.replace(/\D/g, '').slice(0, 8) || null,
                    accountingArea: (form.accountingArea ?? base.accountingArea)?.trim() || null,
                    vatNumber: (form.vatNumber ?? base.vatNumber)?.trim() || null,

                    // keep flags + parent
                    autoInvoice: Boolean(base.autoInvoice),
                    active: Boolean(base.active),
                    invoiceTemplate: base.invoiceTemplate ?? null,
                    parentId: Number(base.parentId ?? 1),

                    // keep services unless you edit them in the wizard
                    bankServices: (base.bankServices ?? []) as any,
                  }

                  await updateBank(selected.id, payload)

                  const fresh = await getUnionBankById(selected.id)
                  replaceBank(fresh)
                  setSelected(fresh)
                  setBankDetails(fresh)

                  toast.success('Bank aktualisiert', 'Ihre Änderungen wurden gespeichert.')
                  setEditOpen(false)
                }}
              />

              <UnionDeleteBankDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                bankId={selected.id}
                onDeleted={async () => {
                  toast.success('Bank gelöscht', 'Die Bank wurde gelöscht.')
                  removeBank(selected.id)
                  setSelected(null)
                  setBankDetails(null)
                }}
              />
              <UnionBankDiscountDialog
                open={discountOpen}
                onOpenChange={setDiscountOpen}
                loading={discountLoading}
                currentPercentage={discountPct}
                onSubmit={async pct => {
                  if (!selected) return

                  if (!discountExists) {
                    await createBankDiscount({ bankId: selected.id, percentage: pct })
                    setDiscountExists(true)
                  } else {
                    await upsertBankDiscount(selected.id, { percentage: pct })
                  }

                  toast.success('Rabatt gespeichert', 'Der Rabatt wurde erfolgreich gespeichert.')
                  setDiscountPct(pct)
                  setDiscountOpen(false)
                }}
                onDelete={async () => {
                  if (!selected) return
                  await deleteBankDiscount(selected.id)
                  toast.success('Rabatt entfernt', 'Der Rabatt wurde gelöscht.')
                  setDiscountExists(false)
                  setDiscountPct(null)
                  setDiscountOpen(false)
                }}
              />
            </Box>
          )}
          <UnionBankWizardDialog
            mode="create"
            open={createOpen}
            onOpenChange={setCreateOpen}
            initial={null}
            onSubmit={async form => {
              // IMPORTANT: backend not ready → send only known Bank fields for now

              const payload: CreateOrUpdateBankPayload = {
                name: form.name,
                name2: form.name2?.trim() || null,
                name3: form.name3?.trim() || null,

                street: toNullable(form.street),
                streetNumber: toNullable(form.streetNumber),
                postal: toNullable(form.postal),
                salesRegion: toNullable(form.salesRegion),
                city: toNullable(form.city),
                country: toNullable(form.country),

                // contactDetails is user-specific — Union must not touch it (SIB-876)
                contactDetails: null,
                internalContactEmail: toNullable(form.internalContactEmail),

                sapDebtorId: form.sapDebtorId?.trim() || null,
                businessPartner: form.businessPartner?.trim() || null,
                accountingArea: form.accountingArea?.trim() || null,
                blzNum: form.blz?.trim() || null,
                centralBookingBlock: form.centralBookingBlock?.trim() || null,
                vatNumber: null, // add field in wizard if needed

                autoInvoice: false, // or take from backend if you manage it
                active: true, // for create default true; for edit use selected.active
                invoiceTemplate: null, // keep as null unless you support it
                parentId: 1,

                bankServices: [], // later: hook to services UI if needed
              }

              const created = await createBank(payload)
              addBank(created as UnionBank)
              setSelected(created as UnionBank)
              setBankDetails(created as UnionBank)
              toast.success('Bank erstellt', 'Die Bank wurde erfolgreich angelegt.')
            }}
          />
        </Box>
      </Flex>
    </Flex>
  )
}

function DetailRow({ label, value }: { label: string; value: any }) {
  return (
    <Flex fontSize="sm" gap={2} py={2} align="flex-start">
      <Text width="40%" color="fg.muted" fontWeight="normal">
        {label}
      </Text>
      <Text color="fg.muted" fontWeight="normal">
        {value || '—'}
      </Text>
    </Flex>
  )
}
